/**
 * PostHog analytics wrapper for LupusLogix.
 *
 * - Uses the Lovable PostHog connector when available
 *   (VITE_LOVABLE_CONNECTOR_POSTHOG_API_KEY / _REGION),
 *   falling back to legacy VITE_POSTHOG_KEY / VITE_POSTHOG_REGION.
 * - Collection begins at the public launch epoch (2026-08-01T05:00:00Z,
 *   midnight CDT) so the new PostHog project stays clean until launch day.
 * - person_profiles: 'identified_only' (no anonymous profiles)
 * - No-op if key is not set (local dev safe)
 *
 * Event schema (v2) — Active-user metric = any of these events in 24h:
 *   user_signed_up, user_activated, session_started,
 *   symptom_logged, medication_logged, appointment_logged, mood_logged,
 *   subscription_started, subscription_cancelled,
 *   push_notification_received,
 *   feature_used, report_generated, care_team_invited
 */
import posthog from 'posthog-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

let initialized = false;

/** Public launch epoch: August 1, 2026 at midnight CDT (05:00 UTC). */
const LAUNCH_EPOCH_MS = Date.parse('2026-08-01T05:00:00.000Z');

export function initAnalytics(): void {
  if (initialized) return;

  // Launch gate: do not collect analytics before the public launch window.
  if (Date.now() < LAUNCH_EPOCH_MS) return;

  const key =
    (import.meta.env.VITE_LOVABLE_CONNECTOR_POSTHOG_API_KEY as string | undefined) ||
    (import.meta.env.VITE_POSTHOG_KEY as string | undefined);
  if (!key) return;

  const region =
    (import.meta.env.VITE_LOVABLE_CONNECTOR_POSTHOG_REGION as string | undefined) ||
    (import.meta.env.VITE_POSTHOG_REGION as string | undefined) ||
    'us';
  const apiHost = region === 'us' ? 'https://us.i.posthog.com' : 'https://eu.i.posthog.com';

  try {
    posthog.init(key, {
      api_host: apiHost,
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: false, // We track explicit events only for a clean schema
      disable_session_recording: true,
      loaded: (ph) => {
        if (import.meta.env.DEV) ph.debug(false);
      },
    });
    initialized = true;
  } catch {
    // swallow — analytics must never break the app
  }
}

export function identifyUser(
  userId: string,
  props?: { email?: string; betaRound?: 'I' | 'II'; role?: string }
): void {
  if (!initialized) return;
  try {
    posthog.identify(userId, {
      email: props?.email,
      beta_round: props?.betaRound,
      role: props?.role,
    });
  } catch {}
}

export function resetAnalytics(): void {
  if (!initialized) return;
  try {
    posthog.reset();
  } catch {}
}

export function track(event: string, props?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    posthog.capture(event, props);
  } catch {}
}

/** Back-compat alias for legacy call sites (landing pages). */
export const trackEvent = track;

// ---- The 10 named events -------------------------------------------------

export const analytics = {
  userSignedUp: (props?: { method?: 'email' | 'google'; betaRound?: 'I' | 'II' }) =>
    track('user_signed_up', props),

  /** Fire once, the first time a user completes any meaningful log. */
  userActivated: (props?: { firstAction: 'symptom' | 'medication' | 'mood' | 'appointment' }) =>
    track('user_activated', props),

  sessionStarted: (props?: { pwa?: boolean; offline?: boolean }) =>
    track('session_started', props),

  symptomLogged: (props?: { symptomCount?: number; hasPhoto?: boolean }) =>
    track('symptom_logged', props),

  medicationLogged: (props?: { medicationId?: string; asNeeded?: boolean }) =>
    track('medication_logged', props),

  appointmentLogged: (props?: { specialty?: string; hasReminder?: boolean }) =>
    track('appointment_logged', props),

  moodLogged: (props?: { moodValue?: number | string; hasNotes?: boolean }) =>
    track('mood_logged', props),

  subscriptionStarted: (props?: { tier?: string; priceId?: string }) =>
    track('subscription_started', props),

  pricingViewed: (props?: { source?: string }) =>
    track('pricing_view', props),

  checkoutStarted: (props?: { plan?: string; source?: string }) =>
    track('checkout_started', props),

  subscriptionPurchased: (props?: { tier?: string; priceId?: string; source?: string }) =>
    track('subscription_purchased', props),

  emailVerified: (props?: { via?: string }) =>
    track('auth_email_confirmed', props),

  subscriptionCancelled: (props?: { tier?: string; reason?: string }) =>
    track('subscription_cancelled', props),

  pushNotificationReceived: (props?: { type?: string; delivered?: boolean }) =>
    track('push_notification_received', props),

  /** Generic per-feature usage — feeds "power user %" cohorts. */
  featureUsed: (props: { feature: string; area?: string }) =>
    track('feature_used', props),

  /** Fired when the user exports/generates a PDF report. */
  reportGenerated: (props?: { reportType?: string; days?: number; hasPhotos?: boolean }) =>
    track('report_generated', props),

  /** Fired when a user invites a care-team member (virality signal). */
  careTeamInvited: (props?: { channel?: 'email' | 'sms' | 'share'; relation?: string }) =>
    track('care_team_invited', props),
};

/**
 * Fire `user_activated` exactly once per user (first meaningful log).
 * Uses the server-side `mark_user_activated` RPC (backed by user_activation
 * table + unique PK) so activation numbers can't be reset by clearing storage
 * or reinstalling. Emits the PostHog event only on the initial insert.
 */
export async function markActivatedOnce(
  firstAction: 'symptom' | 'medication' | 'mood' | 'appointment'
): Promise<void> {
  try {
    const { data, error } = await supabase.rpc('mark_user_activated', {
      p_first_action: firstAction,
    });
    if (error) { logger.warn('[analytics] mark_user_activated failed', error); return; }
    if (data === true) analytics.userActivated({ firstAction });
  } catch (e) {
    logger.warn('[analytics] markActivatedOnce error', e);
  }
}